/**
 * Reorder page controller — manages product selection, delivery, and checkout.
 */
export class Reorder {
    /**
     * Creates a new Reorder instance.
     *
     * @param {string} productClass - CSS class name for product checkbox elements.
     * @param {string} deliveryId - DOM ID of the delivery element.
     * @param {string} dropdownId - DOM ID of the delivery day dropdown.
     * @param {string} payClass - CSS class name for pay button elements.
     * @param {string} csrf - CSRF token for API requests.
     */
    constructor(productClass, deliveryId, dropdownId, payClass, csrf) {
        /** @type {string} */
        this.csrf = csrf;
        /** @type {HTMLCollectionOf<Element>} */
        this.payButtons = document.getElementsByClassName(payClass);
        /** @type {string} */
        this.payClass = payClass;

        const products = document.getElementsByClassName(productClass);
        const delivery = document.getElementById(deliveryId);
        const dropdown = document.getElementById(dropdownId);

        if (
            products.length === 0 ||
            this.payButtons.length === 0 ||
            !delivery ||
            !dropdown
        ) {
            return;
        }

        this.products(products);
        this.delivery(delivery, dropdown);
        this.missingDeliveryDay(delivery, this.payButtons);
        this.checkout(this.payButtons);
    }

    /**
     * Perform a fetch request with CSRF token and optional JSON body.
     *
     * @param {string} url - The URL to request.
     * @param {string} method - The HTTP method (GET, POST, etc.).
     * @param {Record<string, unknown>} [body] - Optional request body for POST requests.
     * @returns {Promise<Record<string, unknown>>} The parsed JSON response data.
     */
    getData(url, method, body = {}) {
        /** @type {RequestInit} */
        const options = {
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrf ?? "",
            },
            method: method,
        };

        if (method === "POST") {
            options.body = JSON.stringify(body);
        }

        return fetch(url, options)
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }

                return Promise.reject({});
            })
            .then((data) => {
                return data;
            })
            .catch((data) => data);
    }

    /**
     * Update the DOM based on response data — toggle visibility, text, and button states.
     *
     * @param {Record<string, unknown>} data - A map of element IDs to values.
     * @returns {void}
     */
    update(data) {
        if (Object.keys(data).length === 0) return;

        for (const [selector, value] of Object.entries(data)) {
            const type = typeof value;
            const el = document.getElementById(selector);

            if (!el) {
                continue;
            }

            if (el.classList.contains(this.payClass)) {
                for (const button of Array.from(this.payButtons)) {
                    if (!value) {
                        button.setAttribute("disabled", "disabled");
                    } else {
                        button.removeAttribute("disabled");
                    }
                }

                continue;
            }

            if (type === "boolean" && value) {
                el.classList.remove("hide");
            }

            if (type === "boolean" && !value) {
                el.classList.add("hide");
            }

            if (type === "string") {
                el.innerHTML = /** @type {string} */ (value);
            }
        }
    }

    /**
     * Attach change listeners to product checkbox elements.
     *
     * @param {HTMLCollectionOf<Element>} elements - The product checkbox elements.
     * @returns {void}
     */
    products(elements) {
        for (const el of elements) {
            el.addEventListener("change", (/** @type {Event} */ e) => {
                const target = /** @type {HTMLInputElement} */ (e.target);
                const url = "/reorder/update/products";
                /** @type {Record<string, unknown>} */
                const param = {
                    id: target.dataset.productId ?? "",
                    state: target.checked,
                };

                this.getData(url, "POST", param).then((data) => {
                    this.update(data);
                });
            });
        }
    }

    /**
     * Attach a change listener to the delivery day dropdown.
     *
     * @param {HTMLElement} el - The delivery container element.
     * @param {HTMLElement} dropdown - The delivery day dropdown element.
     * @returns {void}
     */
    delivery(el, dropdown) {
        dropdown.addEventListener("change", (/** @type {Event} */ e) => {
            const target = /** @type {HTMLSelectElement} */ (e.target);
            const url = "/reorder/update/delivery";
            const param = { day: target.value };

            this.getData(url, "POST", param).then((data) => {
                this.update(data);
            });
        });
    }

    /**
     * Check initial checkout status and disable pay buttons if needed.
     *
     * @param {HTMLCollectionOf<Element>} payButtons - The pay button elements.
     * @returns {void}
     */
    checkout(payButtons) {
        const url = "/reorder/status/checkout";

        this.getData(url, "GET").then((data) => {
            for (const [selector, value] of Object.entries(data)) {
                const el = document.getElementById(selector);

                if (el?.classList.contains(this.payClass ?? "") && !value) {
                    for (const button of payButtons) {
                        button.setAttribute("disabled", "disabled");
                    }
                }
            }
        });
    }

    /**
     * Attach click listeners on pay button parents to re-check delivery status.
     *
     * @param {HTMLElement} delivery - The delivery container element.
     * @param {HTMLCollectionOf<Element>} payButtons - The pay button elements.
     * @returns {void}
     */
    missingDeliveryDay(delivery, payButtons) {
        for (const button of payButtons) {
            button.parentElement?.addEventListener("click", () => {
                const url = "/reorder/status/delivery";

                this.getData(url, "GET").then((data) => {
                    this.update(data);
                });
            });
        }
    }
}
